import React, { useMemo, useState } from "react";
import GetAppIcon from "@material-ui/icons/GetApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ImageIcon from "@material-ui/icons/Image";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import TableChartIcon from "@material-ui/icons/TableChart";

import api from "../../services/api";
import { getBackendUrl } from "../../config";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
const WORD_EXTENSIONS = ["doc", "docx"];
const EXCEL_EXTENSIONS = ["xls", "xlsx", "xlsm", "csv"];
const PDF_EXTENSIONS = ["pdf"];

const extensionFromName = (name = "") => {
  const cleanName = String(name).split("?")[0].split("#")[0];
  const parts = cleanName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const displayFilename = (message) => {
  const raw = message?.mName || message?.mFilename || message?.mText || "Anexo";
  try {
    return decodeURIComponent(String(raw).split("/").pop());
  } catch (_) {
    return String(raw).split("/").pop();
  }
};

const classifyAttachment = (message) => {
  const mime = String(message?.mMime || message?.mimeType || "").toLowerCase();
  const filename = message?.mName || message?.mFilename || message?.mText || "";
  const ext = extensionFromName(filename);

  if (mime.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (mime === "application/pdf" || PDF_EXTENSIONS.includes(ext)) return "pdf";
  if (
    mime.includes("word") ||
    mime.includes("wordprocessingml") ||
    WORD_EXTENSIONS.includes(ext)
  ) return "word";
  if (
    mime.includes("excel") ||
    mime.includes("spreadsheetml") ||
    mime.includes("csv") ||
    EXCEL_EXTENSIONS.includes(ext)
  ) return "excel";

  return "document";
};

const absoluteAttachmentUrl = (message) => {
  const raw =
    message?.mUrl ||
    message?.mMediaUrl ||
    message?.mediaUrl ||
    message?.mFileUrl ||
    message?.fileUrl ||
    message?.mPath ||
    message?.mFilename ||
    "";

  if (!raw) return "";

  const value = String(raw).trim();
  if (/^(https?:|blob:|data:)/i.test(value)) return value;

  const backend = getBackendUrl().replace(/\/$/, "");
  if (value.startsWith("/")) return `${backend}${encodeURI(value)}`;

  // ATND+/Whaticket-compatible public media convention. If the backend
  // already supplies public/... keep it; otherwise treat mFilename as a
  // file stored under /public/.
  if (value.startsWith("public/")) return `${backend}/${encodeURI(value)}`;
  return `${backend}/public/${encodeURI(value)}`;
};

const iconFor = (kind) => {
  if (kind === "image") return <ImageIcon />;
  if (kind === "pdf") return <PictureAsPdfIcon />;
  if (kind === "word") return <DescriptionIcon />;
  if (kind === "excel") return <TableChartIcon />;
  return <DescriptionIcon />;
};

const labelFor = (kind) => {
  if (kind === "image") return "Imagem";
  if (kind === "pdf") return "PDF";
  if (kind === "word") return "Word";
  if (kind === "excel") return "Excel";
  return "Documento";
};

const AttachmentMessage = ({ message }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const kind = useMemo(() => classifyAttachment(message), [message]);
  const filename = useMemo(() => displayFilename(message), [message]);
  const url = useMemo(() => absoluteAttachmentUrl(message), [message]);

  const openAttachment = () => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadAttachment = async () => {
    if (!url || downloading) return;

    setDownloading(true);
    try {
      const response = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = filename || "anexo";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.warn("[ATND+ attachment] Download fallback to browser", error);
      openAttachment();
    } finally {
      setDownloading(false);
    }
  };

  if (kind === "image" && url && !imageFailed) {
    return (
      <div className="attachment attachment--image">
        <button
          type="button"
          className="attachment-image-button"
          onClick={openAttachment}
          aria-label={`Abrir imagem ${filename}`}
        >
          <img
            className="attachment-image"
            src={url}
            alt={filename || "Imagem recebida"}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </button>
        <div className="attachment-image-caption">
          <span title={filename}>{filename}</span>
          <button type="button" onClick={downloadAttachment} className="attachment-icon-action" title="Baixar">
            <GetAppIcon fontSize="small" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`attachment-card attachment-card--${kind}`}>
      <div className="attachment-file-icon" aria-hidden="true">
        {iconFor(kind)}
      </div>
      <div className="attachment-file-copy">
        <strong title={filename}>{filename}</strong>
        <span>{labelFor(kind)}</span>
      </div>
      <div className="attachment-actions">
        {url && (
          <button type="button" onClick={openAttachment} className="attachment-icon-action" title="Abrir">
            <OpenInNewIcon fontSize="small" />
          </button>
        )}
        {url && (
          <button type="button" onClick={downloadAttachment} className="attachment-icon-action" title="Baixar">
            <GetAppIcon fontSize="small" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AttachmentMessage;
